import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Maximize2, Minimize2, Pause, Play, Volume2, VolumeX } from "lucide-react";
import {
  FRAME_STEP_SECONDS,
  SEEK_STEP_SECONDS,
  VOLUME_STEP,
  bufferedSegments,
  formatPlaybackRate,
  formatPlaybackTime,
  nextPlaybackRate,
  offsetPlayhead,
  readBufferedRanges,
  seekTargetFromRatio,
} from "@/lib/mediaPlayer";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { cn } from "@/lib/utils";

import { toggleMediaFullscreen, type FullscreenVideo } from "@/lib/mediaFullscreen";
import { holdMediaFocus, mediaFocusHeld, subscribeMediaFocus } from "@/lib/mediaFocus";

const CONTROLS_HIDE_MS = 2500;
const PRELOAD_MARGIN = "400px";

interface Props {
  src: string;
  poster?: string | null;
  variant?: "preview" | "theater";
  fit?: "contain" | "cover";
  label?: string;
  className?: string;
  autoPlay?: boolean;
  playOnHover?: boolean;
  playWhenVisible?: boolean;
  /** Hold the network until the tile nears the viewport (grids of many clips).
   *  With a poster the clip is not fetched until it is played. */
  lazyPreload?: boolean;
  durationSeconds?: number | null;
  testId?: string;
  onClick?: () => void;
  /** Theater only: plain ←/→ seek. When false they are left to the parent (e.g. to step
   *  between generations) and seeking moves to Shift+←/→. */
  arrowKeysSeek?: boolean;
  /** Theater only: the parent owns fullscreen; the button, `f` and double-click call this. */
  onToggleFullscreen?: () => void;
  /** Fullscreen state reported by the parent when it owns fullscreen. */
  fullscreenActive?: boolean;
}

export function MediaPlayer({
  src,
  poster,
  variant = "preview",
  fit = "contain",
  label,
  className,
  autoPlay = false,
  playOnHover = false,
  playWhenVisible = false,
  lazyPreload = false,
  durationSeconds,
  testId,
  onClick,
  arrowKeysSeek = true,
  onToggleFullscreen,
  fullscreenActive,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const reducedMotion = useReducedMotion();

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);
  const [buffered, setBuffered] = useState<[number, number][]>([]);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(variant === "preview");
  const [rate, setRate] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState<string | null>(null);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [preload, setPreload] = useState<"none" | "metadata">(lazyPreload ? "none" : "metadata");
  const [framed, setFramed] = useState(false);

  const theater = variant === "theater";
  const objectFit = theater ? "contain" : fit;
  const focusHeld = useSyncExternalStore(subscribeMediaFocus, mediaFocusHeld, () => false);
  const suspended = !theater && focusHeld;

  useEffect(() => {
    if (theater) return holdMediaFocus();
  }, [theater]);

  // React drops the src attribute while suspended; load() then aborts any
  // request the element still holds open.
  useEffect(() => {
    const video = videoRef.current;
    if (!suspended || !video) return;
    video.pause();
    video.load();
  }, [suspended]);

  useEffect(() => {
    const video = videoRef.current as FullscreenVideo | null;
    const onFullscreenChange = () =>
      setFullscreen(document.fullscreenElement === containerRef.current || Boolean(video?.webkitDisplayingFullscreen));
    const onNativeEnter = () => { setFullscreen(true); setFullscreenError(null); };
    const onNativeExit = () => setFullscreen(false);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    video?.addEventListener("webkitbeginfullscreen", onNativeEnter);
    video?.addEventListener("webkitendfullscreen", onNativeExit);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      video?.removeEventListener("webkitbeginfullscreen", onNativeEnter);
      video?.removeEventListener("webkitendfullscreen", onNativeExit);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || preload !== "none" || poster) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setPreload("metadata");
        observer.disconnect();
      },
      { rootMargin: PRELOAD_MARGIN }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [preload, poster]);

  useEffect(() => {
    const container = containerRef.current;
    if (!playWhenVisible || !container || reducedMotion) return;
    const saveData = Boolean(
      (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData
    );
    if (saveData) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        if (!video) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
          void video.play().catch(() => undefined);
        } else {
          video.pause();
        }
      },
      { threshold: [0, 0.55] }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [playWhenVisible, reducedMotion, src]);

  const revealControls = useCallback(() => {
    if (!theater) return;
    setControlsVisible(true);
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setControlsVisible(false);
    }, CONTROLS_HIDE_MS);
  }, [theater]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => undefined);
    else video.pause();
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (onToggleFullscreen) {
      onToggleFullscreen();
      return;
    }
    const container = containerRef.current;
    const video = videoRef.current;
    if (!container || !video) return;
    setFullscreenError(null);
    void toggleMediaFullscreen(container, video, document).catch(() => {
      setFullscreenError("Could not open fullscreen. Try again after the video loads.");
    });
  }, [onToggleFullscreen]);
  const showFullscreen = onToggleFullscreen ? Boolean(fullscreenActive) : fullscreen;

  const seekTo = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = seconds;
    setCurrentTime(seconds);
  }, []);

  const applyVolume = useCallback((next: number) => {
    const video = videoRef.current;
    if (!video) return;
    const clamped = Math.min(Math.max(next, 0), 1);
    video.volume = clamped;
    video.muted = clamped === 0;
    setVolume(clamped);
    setMuted(clamped === 0);
  }, []);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (!theater) return;
    const video = videoRef.current;
    if (!video) return;
    if ((event.key === "ArrowLeft" || event.key === "ArrowRight") && !arrowKeysSeek && !event.shiftKey) return;
    const handled = () => {
      event.preventDefault();
      revealControls();
    };
    switch (event.key) {
      case " ":
      case "k":
        handled();
        togglePlay();
        break;
      case "ArrowRight":
        handled();
        seekTo(offsetPlayhead(video.currentTime, SEEK_STEP_SECONDS, video.duration));
        break;
      case "ArrowLeft":
        handled();
        seekTo(offsetPlayhead(video.currentTime, -SEEK_STEP_SECONDS, video.duration));
        break;
      case ".":
        handled();
        seekTo(offsetPlayhead(video.currentTime, FRAME_STEP_SECONDS, video.duration));
        break;
      case ",":
        handled();
        seekTo(offsetPlayhead(video.currentTime, -FRAME_STEP_SECONDS, video.duration));
        break;
      case "ArrowUp":
        handled();
        applyVolume(volume + VOLUME_STEP);
        break;
      case "ArrowDown":
        handled();
        applyVolume(volume - VOLUME_STEP);
        break;
      case "m":
        handled();
        applyVolume(muted ? 1 : 0);
        break;
      case "f":
        handled();
        toggleFullscreen();
        break;
      default:
        break;
    }
  }

  const segments = bufferedSegments(buffered, duration);
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        "group relative flex items-center justify-center overflow-hidden bg-black",
        theater ? "h-full w-full" : "h-full w-full",
        className
      )}
      onMouseMove={revealControls}
      onMouseLeave={() => {
        if (theater && playing) setControlsVisible(false);
        if (playOnHover) videoRef.current?.pause();
      }}
      onMouseEnter={() => {
        if (playOnHover && !reducedMotion) void videoRef.current?.play().catch(() => undefined);
      }}
      onKeyDown={onKeyDown}
      tabIndex={theater ? 0 : -1}
      role={theater ? "group" : undefined}
      aria-label={theater ? label ?? "Video player" : undefined}
    >
      {fullscreenError && (
        <p role="alert" className="absolute inset-x-3 top-3 z-20 rounded-lg bg-black/80 p-2 text-xs text-white">{fullscreenError}</p>
      )}
      <video
        ref={videoRef}
        data-testid={testId}
        src={suspended ? undefined : src}
        poster={poster ?? undefined}
        className={cn(
          objectFit === "cover" ? "h-full w-full object-cover" : "max-h-full max-w-full object-contain"
        )}
        muted={muted}
        loop={!theater}
        autoPlay={autoPlay && !reducedMotion}
        playsInline
        preload={preload}
        onClick={(event) => {
          if (theater) {
            event.stopPropagation();
            togglePlay();
          } else if (onClick) {
            onClick();
          }
        }}
        onDoubleClick={theater ? toggleFullscreen : undefined}
        onPlay={() => {
          setPlaying(true);
          revealControls();
        }}
        onPause={() => {
          setPlaying(false);
          setControlsVisible(true);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onDurationChange={(event) => setDuration(event.currentTarget.duration)}
        onLoadedMetadata={(event) => {
          setDuration(event.currentTarget.duration);
          event.currentTarget.playbackRate = rate;
          setFramed(true);
        }}
        onProgress={(event) => setBuffered(readBufferedRanges(event.currentTarget.buffered))}
      />

      {!theater && !framed && !poster && (
        <span className="pointer-events-none absolute inset-0 bg-studio-panel motion-safe:animate-pulse" />
      )}

      {!theater && duration > 0 && (
        <span className="pointer-events-none absolute bottom-2 right-2 rounded-md bg-black/70 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-white">
          {formatPlaybackTime(duration)}
        </span>
      )}

      {theater && (
        <div
          className={cn(
            "absolute inset-x-0 bottom-0 flex flex-col gap-2 bg-gradient-to-t from-black/85 to-transparent px-4 pb-3 pt-8",
            !reducedMotion && "transition-opacity duration-200",
            controlsVisible ? "opacity-100" : "pointer-events-none opacity-0"
          )}
          data-no-swipe
          onClick={(event) => event.stopPropagation()}
        >
          <div className="relative h-1.5 w-full">
            <div className="absolute inset-0 rounded-full bg-white/20" />
            {segments.map((segment) => (
              <div
                key={`${segment.left}-${segment.width}`}
                className="absolute inset-y-0 rounded-full bg-white/35"
                style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
              />
            ))}
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-studio-accent"
              style={{ width: `${progress}%` }}
            />
            <input
              type="range"
              min={0}
              max={1}
              step={0.0001}
              value={duration > 0 ? currentTime / duration : 0}
              onChange={(event) => seekTo(seekTargetFromRatio(Number(event.target.value), duration))}
              aria-label="Seek"
              aria-valuetext={`${formatPlaybackTime(currentTime)} of ${formatPlaybackTime(duration)}`}
              className="absolute inset-0 h-full w-full cursor-pointer appearance-none bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            />
          </div>

          <div className="flex items-center gap-3 text-white">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? "Pause" : "Play"}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>

            <span className="font-mono text-xs tabular-nums text-white/80">
              {formatPlaybackTime(currentTime)} / {formatPlaybackTime(duration)}
            </span>

            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const next = nextPlaybackRate(rate);
                  setRate(next);
                  if (videoRef.current) videoRef.current.playbackRate = next;
                }}
                aria-label={`Playback speed ${formatPlaybackRate(rate)}`}
                className="rounded-md px-2 py-1 font-mono text-xs tabular-nums hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {formatPlaybackRate(rate)}
              </button>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => applyVolume(muted ? 1 : 0)}
                  aria-label={muted ? "Unmute" : "Mute"}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={muted ? 0 : volume}
                  onChange={(event) => applyVolume(Number(event.target.value))}
                  aria-label="Volume"
                  aria-valuetext={`${Math.round((muted ? 0 : volume) * 100)} percent`}
                  className="h-1 w-16 cursor-pointer appearance-none rounded-full bg-white/25 accent-studio-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                />
              </div>

              <button
                type="button"
                onClick={toggleFullscreen}
                aria-label={showFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                {showFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
